import { useCallback } from "react";
import { message } from "antd";
import { Hooks } from "@xt/xtbase";

import store from "store";

import { AccountEnum } from "store/balances";

const { useTranslation } = Hooks;

interface OutputProps {
  accountFrom: AccountEnum;
  accountTo: AccountEnum;
  currency: string;
  currencySwitch?: string;
}

const Main = ([
  accountFrom,
  accountTo,
  currency,
  leverSymbol,
  allAccCurrencyObj,
  apiReqBalances,
  accFrom,
  accTo,
  setAccFrom,
  setAccFromBalances,
  setAccTo,
  setAccToBalances,
  setCoin,
  setAmount,
  setLeverMarketName,
]) => {
  const t = useTranslation();

  return useCallback(() => {
    const parseObj = (() => {
      if (accountFrom && accountTo) return handleFromFixedAndToFixed({ accountFrom, accountTo, currency });
      if (accountFrom && !accountTo) return handleFromFixedAndToAuto({ accountFrom, currency });
      if (!accountFrom && accountTo) return handleFromAutoAndToFixed({ accountTo, currency });
      return handleFromAutoAndToAuto({ currency });
    })();

    const coin = parseObj.currencySwitch || parseObj.currency;
    if (parseObj.currencySwitch) message.info(t("trade.transferNoneAndSwitchTip", [coin.toUpperCase()]));
    setCoin(coin);

    if (parseObj.accountFrom === accFrom) {
      apiReqBalances(accFrom, setAccFromBalances);
    } else {
      setAccFrom(parseObj.accountFrom);
    }

    if (parseObj.accountTo === accTo && parseObj.accountTo === AccountEnum.lever) {
      apiReqBalances(accTo, setAccToBalances);
    } else {
      setAccTo(parseObj.accountTo);
    }

    leverSymbol && setLeverMarketName(leverSymbol);
    setAmount("");

    function handleFromFixedAndToFixed({ accountFrom, accountTo, currency, currencySwitch }: OutputProps): OutputProps {
      const fromCurrencyList = allAccCurrencyObj[accountFrom];
      const toCurrencyList = allAccCurrencyObj[accountTo];
      const coin = currencySwitch || currency;

      if (fromCurrencyList.find((obj) => obj.currency === coin) && toCurrencyList.find((obj) => obj.currency === coin)) {
        return { accountFrom, accountTo, currency, currencySwitch };
      }

      if (currencySwitch) return { accountFrom, accountTo, currency };

      const hasFuturesC = accountFrom === AccountEnum.futures_c || accountTo === AccountEnum.futures_c;
      return handleFromFixedAndToFixed({
        accountFrom,
        accountTo,
        currency,
        currencySwitch: hasFuturesC ? "btc" : "usdt",
      });
    }

    function handleFromFixedAndToAuto({
      accountFrom,
      currency,
      currencySwitch,
    }: {
      accountFrom: AccountEnum;
      currency: string;
      currencySwitch?: string;
    }): OutputProps {
      const fromCurrencyList = allAccCurrencyObj[accountFrom];
      const accountToAry = getDefaultAccount(accountFrom);
      const coin = currencySwitch || currency;
      const coinSwitch = accountFrom === AccountEnum.futures_c ? "btc" : "usdt";

      if (!fromCurrencyList.find((obj) => obj.currency === coin)) {
        if (currencySwitch) return { accountFrom, accountTo: accountToAry[0], currency };
        return handleFromFixedAndToAuto({ accountFrom, currency, currencySwitch: coinSwitch });
      }

      for (let i = 0; i < accountToAry.length; i++) {
        const nextAccountTo = accountToAry[i];
        if (allAccCurrencyObj[nextAccountTo].find((obj) => obj.currency === coin)) {
          return { accountFrom, accountTo: nextAccountTo, currency, currencySwitch };
        }
      }

      if (currencySwitch) return { accountFrom, accountTo: accountToAry[0], currency };
      return handleFromFixedAndToAuto({ accountFrom, currency, currencySwitch: coinSwitch });
    }

    function handleFromAutoAndToFixed({
      accountTo,
      currency,
      currencySwitch,
    }: {
      accountTo: AccountEnum;
      currency: string;
      currencySwitch?: string;
    }): OutputProps {
      const toCurrencyList = allAccCurrencyObj[accountTo];
      const accountFromAry = getDefaultAccount(accountTo);
      const coin = currencySwitch || currency;
      const coinSwitch = accountTo === AccountEnum.futures_c ? "btc" : "usdt";

      if (!toCurrencyList.find((obj) => obj.currency === coin)) {
        if (currencySwitch) return { accountFrom: accountFromAry[0], accountTo, currency };
        return handleFromAutoAndToFixed({ accountTo, currency, currencySwitch: coinSwitch });
      }

      for (let i = 0; i < accountFromAry.length; i++) {
        const nextAccountFrom = accountFromAry[i];
        if (allAccCurrencyObj[nextAccountFrom].find((obj) => obj.currency === coin)) {
          return { accountFrom: nextAccountFrom, accountTo, currency, currencySwitch };
        }
      }

      if (currencySwitch) return { accountFrom: accountFromAry[0], accountTo, currency };
      return handleFromAutoAndToFixed({ accountTo, currency, currencySwitch: coinSwitch });
    }

    function handleFromAutoAndToAuto({ currency }: { currency: string }): OutputProps {
      return handleFromFixedAndToAuto({
        accountFrom: AccountEnum.spot,
        currency,
      });
    }

    function getDefaultAccount(skip) {
      const account = [AccountEnum.spot];
      if (store.market.isFuturesUsdtOpen) account.push(AccountEnum.futures_u);
      if (store.market.isFuturesCoinOpen) account.push(AccountEnum.futures_c);
      account.push(AccountEnum.lever);
      if (!store.market.isFuturesUsdtOpen) account.push(AccountEnum.futures_u);
      if (!store.market.isFuturesCoinOpen) account.push(AccountEnum.futures_c);
      return account.filter((acc) => acc !== skip);
    }
  }, [accountFrom, accountTo, currency, leverSymbol, allAccCurrencyObj, apiReqBalances, accFrom, accTo, setAccFrom, setAccFromBalances, setAccTo, setAccToBalances, setCoin, setAmount, setLeverMarketName, t]);
};

export default Main;
